import { useState } from "react";
import { Link } from "react-router-dom";
import "./ultimateTexasHoldem.css";
import preflopChartData from "./uthPreflopChartData.json";

const SHAPES = [
  { id: "all", label: "All" },
  { id: "o", label: "Offsuit" },
  { id: "s", label: "Suited" },
  { id: "p", label: "Pairs" },
];

const DEAD_COUNTS = preflopChartData.meta.knownDeadCounts;
const BLOCKER_COUNTS = [0, 1, 2, 3];

export function getPreflopChartCell(
  handName,
  knownDead = 10,
  highDead = 0,
  lowDead = 0
) {
  return preflopChartData.hands[handName]?.cells.find(
    (cell) =>
      cell.knownDead === knownDead &&
      cell.highDead === highDead &&
      cell.lowDead === lowDead
  );
}

export function getChartCellStatus(cell) {
  if (!cell) return "unavailable";
  return cell.delta > 0 ? "raise" : "check";
}

export function handHasPositiveCell(hand) {
  return hand.cells.some((cell) => cell.delta > 0);
}

export function handCanExpand(hand) {
  const extraBlockerCells = hand.cells.filter(
    (cell) => cell.highDead + cell.lowDead > 0
  );

  return hand.side === "raise"
    ? extraBlockerCells.some((cell) => cell.delta <= 0)
    : extraBlockerCells.some((cell) => cell.delta > 0);
}

function formatEdge(value, digits = 1) {
  const percent = value * 100;
  return `${percent > 0 ? "+" : ""}${percent.toFixed(digits)}`;
}

function EdgeCell({ cell, compact = false }) {
  if (!cell) {
    return compact ? (
      <span className="uthEdgePreview uthEdgeUnavailable">—</span>
    ) : (
      <td className="uthEdgeCell uthEdgeUnavailable">—</td>
    );
  }

  const status = getChartCellStatus(cell);
  const action = status === "raise" ? "4×" : "Check";
  const title = cell.exact
    ? `${formatEdge(cell.delta, 2)}% of ante; ${action}; exact enumeration`
    : `${formatEdge(cell.delta, 2)}% of ante; ${action}; sampling margin ±${formatEdge(
        1.96 * cell.se,
        2
      ).replace("+", "")}%`;
  const className = `uthEdge${compact ? "Preview" : "Cell"} uthEdge${
    status[0].toUpperCase() + status.slice(1)
  }`;

  if (compact) {
    return (
      <span className={className} title={title}>
        <strong>{formatEdge(cell.delta)}</strong>
        <small>{action}</small>
      </span>
    );
  }

  return (
    <td className={className} title={title}>
      <strong>{formatEdge(cell.delta)}</strong>
      <small>{action}</small>
    </td>
  );
}

function BlockerLabel({ handName, shape, highDead, lowDead }) {
  if (shape === "p") {
    return (
      <span className="uthBlockerPairLabel">
        <b>{highDead}</b> set cards dead
      </span>
    );
  }

  return (
    <span className="uthBlockerLabel">
      <span>
        <b>{handName[0]} dead: {highDead}</b>
      </span>
      <span>
        <b>{handName[1]} dead: {lowDead}</b>
      </span>
    </span>
  );
}

function HandTable({ handName, hand }) {
  const rows =
    hand.shape === "p"
      ? [0, 1, 2].map((highDead) => ({ highDead, lowDead: 0 }))
      : BLOCKER_COUNTS.flatMap((highDead) =>
          BLOCKER_COUNTS.map((lowDead) => ({ highDead, lowDead }))
        );

  return (
    <div className="uthHandTableScroller" role="region" aria-label={`${handName} matching cards table, scroll horizontally`} tabIndex={0}>
      <table className="uthHandTable">
        <thead>
          <tr>
            <th scope="col">Matching cards dead</th>
            {DEAD_COUNTS.map((knownDead) => (
              <th scope="col" key={knownDead}>{knownDead}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ highDead, lowDead }) => (
            <tr key={`${highDead}-${lowDead}`}>
              <th scope="row">
                <BlockerLabel
                  handName={handName}
                  shape={hand.shape}
                  highDead={highDead}
                  lowDead={lowDead}
                />
              </th>
              {DEAD_COUNTS.map((knownDead) => (
                <EdgeCell
                  cell={getPreflopChartCell(
                    handName,
                    knownDead,
                    highDead,
                    lowDead
                  )}
                  key={knownDead}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HandChart({ handName, hand }) {
  const shapeLabel =
    hand.shape === "p" ? "Pair" : hand.shape === "s" ? "Suited" : "Offsuit";
  const canExpand = handCanExpand(hand);
  const collapsedLabel = `Extra blockers stay ${
    hand.side === "raise" ? "4×" : "Check"
  }`;
  const previewCells = DEAD_COUNTS.map((knownDead) => (
    <EdgeCell
      compact
      cell={getPreflopChartCell(handName, knownDead, 0, 0)}
      key={knownDead}
    />
  ));

  if (!canExpand) {
    return (
      <div className="uthChartHand uthChartHandStatic">
        <div className="uthChartHandSummary">
          <span className="uthChartHandIdentity">
            <strong>{handName}</strong>
            <small>{shapeLabel}</small>
          </span>
          {previewCells}
          <span
            className="uthChartNoExpand"
            aria-label={collapsedLabel}
            title={collapsedLabel}
          >
            —
          </span>
        </div>
      </div>
    );
  }

  return (
    <details className="uthChartHand">
      <summary>
        <span className="uthChartHandIdentity">
          <strong>{handName}</strong>
          <small>{shapeLabel}</small>
        </span>
        {previewCells}
        <span className="uthChartChevron" aria-hidden="true">+</span>
      </summary>
      <HandTable handName={handName} hand={hand} />
    </details>
  );
}

function UltimateTexasHoldemChartPage() {
  const [side, setSide] = useState("check");
  const [shape, setShape] = useState("all");
  const hands = Object.entries(preflopChartData.hands).filter(
    ([, hand]) =>
      hand.side === side &&
      handHasPositiveCell(hand) &&
      (shape === "all" || hand.shape === shape)
  );

  return (
    <main className="uthPage uthChartPage">
      <div className="uthShell">
        <header className="uthTopbar">
          <div className="uthBrand">
            <Link to="/" aria-label="Back to home">H</Link>
            <div>
              <span>Ultimate Texas Hold’em</span>
              <strong>Pre-flop blocker chart</strong>
            </div>
          </div>
          <Link className="uthHeaderLink" to="/ultimate-texas-holdem">
            Trainer
          </Link>
        </header>

        <section className="uthChartBoard" aria-labelledby="uth-chart-title">
          <header className="uthChartTitlebar">
            <div>
              <h1 id="uth-chart-title">Estimated blocker edge</h1>
              <p>4× minus check, % of ante.</p>
            </div>
            <div className="uthChartLegend" aria-label="Chart colors">
              <span><i className="uthLegendRaise" /> 4×</span>
              <span><i className="uthLegendCheck" /> Check</span>
            </div>
          </header>

          <div className="uthChartControls">
            <div className="uthChartSideTabs" aria-label="Strategy side">
              <button
                className={side === "check" ? "uthChartControlActive" : ""}
                type="button"
                aria-pressed={side === "check"}
                onClick={() => setSide("check")}
              >
                Marginal checks
              </button>
              <button
                className={side === "raise" ? "uthChartControlActive" : ""}
                type="button"
                aria-pressed={side === "raise"}
                onClick={() => setSide("raise")}
              >
                Marginal 4×
              </button>
            </div>
            <div className="uthChartShapeTabs" aria-label="Hand type">
              {SHAPES.map((option) => (
                <button
                  className={shape === option.id ? "uthChartShapeActive" : ""}
                  type="button"
                  key={option.id}
                  aria-pressed={shape === option.id}
                  onClick={() => setShape(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <p className="uthChartScrollHint">Swipe or scroll sideways to see every dead-card count.</p>
          <div className="uthChartOverview" role="region" aria-label="Preflop strategy chart, scroll horizontally" tabIndex={0}>
            <div className="uthDeadCountHeader" aria-hidden="true">
              <span>Hand · no matching cards dead</span>
              {DEAD_COUNTS.map((count) => <strong key={count}>{count} dead</strong>)}
              <i />
            </div>

            <div className="uthChartHands">
              {hands.map(([handName, hand]) => (
                <HandChart handName={handName} hand={hand} key={handName} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

export default UltimateTexasHoldemChartPage;
